import { MENU_ITEM_KEYS } from './constants.js';
const KNOWN = new Set(MENU_ITEM_KEYS);
/**
 * The menu a tenant gets before anyone opens the branding screen.
 *
 * Trips first, deliberately. A passenger opens this app far more often to find a ticket
 * they already hold than to buy a new one, and the first tab is where the thumb lands.
 *
 * `about` ships hidden: most operators have nothing to put there, and an empty page in the
 * menu is worse than no page. It stays in the list so switching it on is one toggle.
 */
export const DEFAULT_MENU_ITEMS = [
    { key: 'bookings', label: 'My Trips', visible: true },
    { key: 'search', label: 'Book', visible: true },
    { key: 'travellers', label: 'My Travellers', visible: true },
    { key: 'profile', label: 'Profile', visible: true },
    // Ships HIDDEN, like `about`. Bottom tabs stop being usable past about five, and not every
    // operator runs promotions — a tab leading to an empty offers page is worse than no tab.
    // The platform switches it on per tenant when a campaign starts (DECISIONS D69, D70).
    { key: 'offers', label: 'Offers', visible: false },
    { key: 'help', label: 'Help', visible: true },
    { key: 'about', label: 'About', visible: false },
];
/**
 * Turns whatever menu is stored on a tenant into the list the app can rely on.
 *
 * Same contract as `resolveCustomerFeatures`: lenient on read, strict on write. This runs
 * inside the tenant context endpoint, which is the request the entire customer app boots
 * from, so an unrecognised key written by a newer deploy is dropped rather than allowed to
 * take an operator's booking site down.
 *
 * A saved menu that predates a key is completed rather than treated as exhaustive. Without
 * that, every operator who had ever touched the branding screen would be permanently
 * missing every page added afterwards — which is exactly how `travellers` shipped
 * unreachable: the screen existed, no menu could name it.
 */
export function resolveMenuItems(stored) {
    const rows = Array.isArray(stored) ? stored : [];
    const ordered = [];
    for (const entry of rows) {
        if (!entry || typeof entry !== 'object')
            continue;
        const { key, label, visible } = entry;
        if (typeof key !== 'string' || !KNOWN.has(key))
            continue;
        if (ordered.some((o) => o.key === key))
            continue; // first wins; duplicates cannot both apply
        const fallback = DEFAULT_MENU_ITEMS.find((m) => m.key === key);
        ordered.push({
            key: key,
            label: typeof label === 'string' && label.trim() ? label.trim() : (fallback?.label ?? key),
            // Unlike a customer feature, an absent flag here means shown. A menu row nobody
            // decided about is a page the operator has, and hiding it silently loses a tab.
            visible: visible === false ? false : true,
        });
    }
    if (ordered.length === 0)
        return DEFAULT_MENU_ITEMS.map((item) => ({ ...item }));
    // A key the operator has never seen is inserted where the platform's own order puts it
    // relative to the keys they *do* have, rather than appended. Appending would file
    // "My Travellers" after "Help", which reads as an afterthought; this keeps their
    // deliberate ordering intact while landing the new tab somewhere sensible.
    for (const [index, fallback] of DEFAULT_MENU_ITEMS.entries()) {
        if (ordered.some((o) => o.key === fallback.key))
            continue;
        const precedingKeys = DEFAULT_MENU_ITEMS.slice(0, index).map((m) => m.key);
        let at = 0;
        for (const key of precedingKeys) {
            const found = ordered.findIndex((o) => o.key === key);
            if (found >= 0)
                at = Math.max(at, found + 1);
        }
        ordered.splice(at, 0, { ...fallback });
    }
    return ordered;
}
//# sourceMappingURL=menu-items.js.map